import { debtorHeadCells } from '@/components/tables/DebtorsTable';
import {
	prospectHeadCells,
	sharedHeadCells as clientSharedHeadCells,
} from '@/components/tables/ClientTable/data';

export const NEXT_TASK_DATE_HEAD_CELL = {
	id: 'nextTaskDate',
	numeric: false,
	label: 'Next task date',
	align: 'left',
	date: true,
	sortable: true,
};

// Cells shared by the deal, renewal and claim tables; each array below picks them in its own order.
const sharedHeadCells = {
	dealName: { id: 'dealName', label: 'Deal name', sortable: true },
	clientName: { id: 'clientName', label: 'Client name', sortable: true },
	contactName: { id: 'contactName', label: 'Contact name', sortable: true },
	clientOwnerDocId: { id: 'clientOwnerDocId', label: 'Client owner', sortable: true },
	ownerDocId: { id: 'ownerDocId', label: 'Deal assignee', sortable: true },
	createDate: { id: 'createDate', label: 'Created date', sortable: true },
	renewalDate: { id: 'renewalDate', label: 'Renewal date', sortable: true },
	dealStage: { id: 'dealStage', label: 'Stage', sortable: true },
	lossReason: { id: 'lossReason', label: 'Loss reason', sortable: false },
};

export const dealHeadCells = [
	sharedHeadCells.dealName,
	sharedHeadCells.clientName,
	sharedHeadCells.contactName,
	sharedHeadCells.clientOwnerDocId,
	sharedHeadCells.ownerDocId,
	sharedHeadCells.createDate,
	sharedHeadCells.renewalDate,
	sharedHeadCells.dealStage,
	sharedHeadCells.lossReason,
];

export const renewalHeadCells = [
	sharedHeadCells.dealName,
	sharedHeadCells.clientName,
	sharedHeadCells.contactName,
	sharedHeadCells.clientOwnerDocId,
	{ id: 'policies', label: 'Associated policies', sortable: false },
	sharedHeadCells.ownerDocId,
	sharedHeadCells.createDate,
	sharedHeadCells.renewalDate,
	sharedHeadCells.dealStage,
	sharedHeadCells.lossReason,
];

export const endorsementHeadCells = [
	sharedHeadCells.dealName,
	sharedHeadCells.clientName,
	sharedHeadCells.contactName,
	sharedHeadCells.clientOwnerDocId,
	sharedHeadCells.ownerDocId,
	{ id: 'policies', label: 'Associated policy', sortable: false },
	sharedHeadCells.createDate,
	sharedHeadCells.dealStage,
	sharedHeadCells.lossReason,
];

export const claimHeadCells = [
	{ ...sharedHeadCells.dealName, label: 'Claim name' },
	{ id: 'claimId', label: 'ID', sortable: true },
	sharedHeadCells.clientName,
	sharedHeadCells.contactName,
	sharedHeadCells.clientOwnerDocId,
	{ id: 'claimType', label: 'Subject of loss', sortable: true },
	{ id: 'claimNumber', label: 'Claim number', sortable: true },
	{ id: 'insurer', label: 'Insurer', sortable: true },
	{ ...sharedHeadCells.ownerDocId, label: 'Assignee' },
	{ id: 'dateOfLoss', label: 'Date of loss', sortable: true },
	{ ...NEXT_TASK_DATE_HEAD_CELL },
	sharedHeadCells.createDate,
	sharedHeadCells.dealStage,
];

const removeCell = (cells, id) => {
	const index = cells.findIndex((cell) => cell.id === id);
	if (index !== -1) cells.splice(index, 1);
};

// Inserts before the cell with this id, or at the end if there isn't one.
const insertBefore = (cells, id, ...newCells) => {
	const index = cells.findIndex((cell) => cell.id === id);
	cells.splice(index === -1 ? cells.length : index, 0, ...newCells);
};

const getPipelineHeadCells = (pipeline) => {
	switch (pipeline) {
		case process.env.REACT_APP_PIPELINE_CLAIMS:
			return claimHeadCells;
		case process.env.REACT_APP_PIPELINE_DEBTORS:
			return debtorHeadCells;
		case process.env.REACT_APP_PIPELINE_RENEWALS:
			return renewalHeadCells;
		case process.env.REACT_APP_PIPELINE_ENDORSEMENTS:
			return endorsementHeadCells;
		default:
			return dealHeadCells;
	}
};

/**
 * Builds the deal/ticket table column definitions for a pipeline.
 * Shared by list view and kanban sort options so they stay aligned.
 */
export const getDealHeadCells = ({
	pipeline,
	tab,
	showNewBusinessKPI = false,
	showInformedConsent = false,
} = {}) => {
	const isClaims = pipeline == process.env.REACT_APP_PIPELINE_CLAIMS;
	const isNewBusiness = pipeline == process.env.REACT_APP_PIPELINE_NEW_BUSINESS;
	const isEndorsement = pipeline == process.env.REACT_APP_PIPELINE_ENDORSEMENTS;
	const isDebtors = pipeline === process.env.REACT_APP_PIPELINE_DEBTORS;
	const isRenewals = pipeline == process.env.REACT_APP_PIPELINE_RENEWALS;

	const cells = getPipelineHeadCells(pipeline).slice();

	if (isClaims && tab === 'closed') {
		const stageIndex = cells.findIndex((cell) => cell.id === 'dealStage');
		cells.splice(stageIndex, 1, { id: 'dateClosed', label: 'Date Closed', sortable: true });
	}
	if (isNewBusiness && showNewBusinessKPI) {
		insertBefore(
			cells,
			'createDate',
			{ id: 'dealAmount', label: 'Deal amount', sortable: true },
			{ id: 'estimatedCloseDate', label: 'Estimated close date', sortable: true },
		);
		removeCell(cells, 'renewalDate');
	}
	if (isDebtors) {
		removeCell(cells, 'invoices');
	}
	if (showInformedConsent && (isNewBusiness || isEndorsement || isRenewals)) {
		insertBefore(cells, 'dealStage', {
			id: 'informedConsentStatus',
			label: 'Informed Consent',
			sortable: true,
		});
	}

	return cells;
};

/**
 * Exact same cells as the list view, plus next task date.
 * Prospects use the sortable prospect table columns, except stage (the kanban columns are stages).
 */
export const getKanbanSortHeadCells = ({ pipeline, showNewBusinessKPI = false } = {}) => {
	if (pipeline === process.env.REACT_APP_PIPELINE_PROSPECTS) {
		return [...clientSharedHeadCells, ...prospectHeadCells]
			.filter((cell) => cell.sortable && cell.id !== 'stage')
			.map((cell) => (cell.id === 'name' ? { ...cell, label: 'Prospect name' } : cell));
	}

	const cells = getDealHeadCells({ pipeline, showNewBusinessKPI }).filter(
		(cell) => cell.sortable,
	);

	if (!cells.some((cell) => cell.id === 'nextTaskDate')) {
		const createDateIndex = cells.findIndex((cell) => cell.id === 'createDate');
		const insertAt =
			createDateIndex !== -1 ? createDateIndex + 1 : Math.max(cells.length - 1, 0);
		cells.splice(insertAt, 0, { ...NEXT_TASK_DATE_HEAD_CELL });
	}

	return cells;
};
